import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Link as IconLink,
  Unlink as IconUnlink,
  List,
  Undo,
  Redo,
  ListOrdered,
  Eye,
  Braces
} from 'lucide-react';
import { Button as ShadcnButton } from '../../ui/button';
import LinkModal from './LinkModal/LinkModal';
import VariableDropdown from './VariableDropdown/VariableDropdown';
import IntentSlot from '../../../types/BotBuilder/IntentSlot';
import Slot from '../../../types/BotBuilder/Slot';
import { SystemVariableDto } from '../../../services/SystemVariableService/SystemVariableService';
import { toggleListOnCurrentLine } from '../richTextEditorUtils';

interface ToolbarProps {
  editor: Editor;
  onInsertVariable?: (
    varName: string,
    isSystem: boolean,
    isGlobal?: boolean
  ) => void;
  onPreview?: () => void;
  intentSlots?: IntentSlot[];
  globalSlots?: Slot[];
  systemVariables: SystemVariableDto[];
  outputFormat?: 'markdown' | 'plaintext';
}

function Toolbar({
  editor,
  onInsertVariable,
  onPreview,
  intentSlots = [],
  globalSlots = [],
  systemVariables = [],
  outputFormat = 'markdown'
}: Readonly<ToolbarProps>): React.ReactElement {
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);

  const handleToolbarClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className='bg-white relative rounded-lg pb-2'
      onClick={handleToolbarClick}
    >
      <div className='flex flex-wrap items-center gap-1'>
        <div className='flex items-center gap-1 flex-wrap flex-1'>
          {outputFormat === 'markdown' && (
            <>
              <ShadcnButton
                variant='outline'
                size='sm'
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`w-7 h-7 p-0 ${
                  editor.isActive('bold')
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100'
                }`}
                title='Bold'
              >
                <Bold size={14} />
              </ShadcnButton>
              <ShadcnButton
                variant='outline'
                size='sm'
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={`w-7 h-7 p-0 ${
                  editor.isActive('italic')
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100'
                }`}
                title='Italic'
              >
                <Italic size={14} />
              </ShadcnButton>
              <ShadcnButton
                variant='outline'
                size='sm'
                onClick={() => toggleListOnCurrentLine(editor, 'bulletList')}
                className={`w-7 h-7 p-0 ${
                  editor.isActive('bulletList')
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100'
                }`}
                title='Bulleted list'
              >
                <List size={14} />
              </ShadcnButton>
              <ShadcnButton
                variant='outline'
                size='sm'
                onClick={() => toggleListOnCurrentLine(editor, 'orderedList')}
                className={`w-7 h-7 p-0 ${
                  editor.isActive('orderedList')
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100'
                }`}
                title='Numbered list'
              >
                <ListOrdered size={14} />
              </ShadcnButton>
              <ShadcnButton
                variant='outline'
                size='sm'
                onClick={() => setShowLinkModal(!showLinkModal)}
                disabled={editor.state.selection.empty}
                className={`w-7 h-7 p-0 ${
                  editor.isActive('link')
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-100'
                }`}
                title='Link'
              >
                <IconLink size={14} />
              </ShadcnButton>
              <ShadcnButton
                variant='outline'
                size='sm'
                onClick={() => editor.chain().focus().unsetLink().run()}
                disabled={
                  !editor.isActive('link') || editor.state.selection.empty
                }
                className='w-7 h-7 p-0 hover:bg-gray-100'
                title='Unlink'
              >
                <IconUnlink size={14} />
              </ShadcnButton>
            </>
          )}
          <ShadcnButton
            variant='outline'
            size='sm'
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className='w-7 h-7 p-0 hover:bg-gray-100'
            title='Undo'
          >
            <Undo size={14} />
          </ShadcnButton>
          <ShadcnButton
            variant='outline'
            size='sm'
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className='w-7 h-7 p-0 hover:bg-gray-100'
            title='Redo'
          >
            <Redo size={14} />
          </ShadcnButton>
          {onInsertVariable && (
            <div className='variable-dropdown-trigger'>
              <VariableDropdown
                intentSlots={intentSlots}
                globalSlots={globalSlots}
                systemVariables={systemVariables}
                onSelect={(varName, isSystem, isGlobal) => {
                  onInsertVariable(varName, isSystem, isGlobal);
                }}
                triggerIcon={<Braces size={14} />}
                triggerClassName='w-7 h-7 p-0 hover:bg-gray-100 border border-gray-300 text-xs justify-center'
                title='Insert variable'
              />
            </div>
          )}
        </div>
        {onPreview && (
          <div className='flex items-center'>
            <ShadcnButton
              variant='outline'
              size='sm'
              onClick={() => {
                onPreview();
              }}
              className='w-7 h-7 p-0 hover:bg-gray-100'
              title='Preview mode'
            >
              <Eye size={14} />
            </ShadcnButton>
          </div>
        )}
      </div>
      {showLinkModal && outputFormat === 'markdown' && (
        <LinkModal
          editor={editor}
          showLinkModal={showLinkModal}
          setShowLinkModal={setShowLinkModal}
        />
      )}
    </div>
  );
}

export default Toolbar;
